import { z } from 'zod';

import zodLocales from 'common/zod/locales';

import locales from '../../locales';
import { parseAmount, validateTransferLabel } from '../../utils';

const POSITIVE_AMOUNT_REGEX = /^([0-9]+([.,][0-9]*)?|[.,][0-9]+)[.,]?$/;

const NON_ZERO_AMOUNT_REGEX = /^(?!0[.,]00$)^(?!0$).*$/;

export const MAX_LABEL_LENGTH = 100;

const lang = locales.getLanguage();
export const internalTransferDetailsFormSchema = z
  .object({
    amount: z.preprocess(
      (val) => {
        const amount = parseAmount(String(val), lang);
        return amount === undefined ? undefined : String(amount);
      },
      z
        .string({
          required_error: zodLocales.errors.required,
        })
        .regex(POSITIVE_AMOUNT_REGEX, zodLocales.errors.amount)
        .regex(NON_ZERO_AMOUNT_REGEX, zodLocales.errors.nonZeroAmount),
    ),

    /**
     * Readonly field, only there for balance comparison.
     */
    balance: z.number().nullish(),

    label: z
      .string()
      .max(
        MAX_LABEL_LENGTH,
        locales.formatString(locales.labelMaxLength, MAX_LABEL_LENGTH),
      )
      .refine(validateTransferLabel, {
        message: locales.labelError,
      }),
  })

  .superRefine(({ amount, balance }, ctx) => {
    const parsedAmount = (amount && parseAmount(amount, lang)) || 0;
    if (typeof balance !== 'number' || parsedAmount > balance) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: locales.insufficientFunds,
        path: ['amount' satisfies keyof InternalTransferDetailsFormSchema],
      });
    }
  });

export type InternalTransferDetailsFormSchema = z.infer<
  typeof internalTransferDetailsFormSchema
>;
